import React, { useEffect } from "react";
import { Breadcrumb, Result, Tabs, Tag, Typography } from "antd";
import {
  BarChartOutlined,
  PictureOutlined,
  RobotOutlined,
  ShoppingCartOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { AcquistoAddOn, CreditiAI, CreditiAICharts } from "../../CreditiAI";
import OrgCrediti from "../../OrgDetailsV2/tabs/AI/OrgCrediti";
import GiottoAiResults from "../GiottoAiResults";
import {
  buildGiottoAiHubSearch,
  getGiottoAiHubView,
} from "./hubViews";
import "./GiottoAiHub.css";

const { Paragraph, Text, Title } = Typography;

const tabLabel = (icon, label) => (
  <span className="giotto-ai-hub__tab-label">
    {icon}
    {label}
  </span>
);

const GiottoAiHub = ({ mode, orgId = null }) => {
  const history = useHistory();
  const location = useLocation();
  const isGlobalMode = mode === "global";
  const isOrgMode = mode === "org";
  const validMode = isGlobalMode || isOrgMode;
  const validOrgScope = !isOrgMode || Boolean(orgId);
  const activeView = getGiottoAiHubView(location.search, mode);
  const requestedView = new URLSearchParams(location.search).get("view");

  useEffect(() => {
    if (!validMode || !validOrgScope || !requestedView || requestedView === activeView) {
      return;
    }

    history.replace({
      pathname: location.pathname,
      search: buildGiottoAiHubSearch(location.search, activeView, mode),
    });
  }, [
    activeView,
    history,
    location.pathname,
    location.search,
    requestedView,
    mode,
    validMode,
    validOrgScope,
  ]);

  if (!validMode) {
    return <Result status="error" title="Modalità Giotto AI non valida" />;
  }

  if (!validOrgScope) {
    return (
      <Result
        status="error"
        title="Organizzazione non disponibile"
        subTitle="La vista organizzazione richiede un orgId valido."
      />
    );
  }

  const handleViewChange = (view) => {
    history.replace({
      pathname: location.pathname,
      search: buildGiottoAiHubSearch(location.search, view, mode),
    });
  };

  const globalItems = [
    {
      key: "credits",
      label: tabLabel(<WalletOutlined />, "Crediti e saldi"),
      children: <CreditiAI embedded />,
    },
    {
      key: "statistics",
      label: tabLabel(<BarChartOutlined />, "Statistiche"),
      children: <CreditiAICharts embedded />,
    },
    {
      key: "purchases",
      label: tabLabel(<ShoppingCartOutlined />, "Acquisti add-on"),
      children: <AcquistoAddOn embedded />,
    },
    {
      key: "results",
      label: tabLabel(<PictureOutlined />, "Risultati"),
      children: <GiottoAiResults embedded mode="global" />,
    },
  ];

  const orgItems = [
    {
      key: "credits",
      label: tabLabel(<WalletOutlined />, "Crediti, ordini e statistiche"),
      children: <OrgCrediti orgId={orgId} />,
    },
    {
      key: "results",
      label: tabLabel(<PictureOutlined />, "Risultati"),
      children: <GiottoAiResults embedded mode="org" orgId={orgId} />,
    },
  ];

  return (
    <div className={`giotto-ai-hub giotto-ai-hub--${mode}`}>
      {isGlobalMode && (
        <Breadcrumb style={{ marginBottom: 16 }}>
          <Breadcrumb.Item>Giotto</Breadcrumb.Item>
          <Breadcrumb.Item>AI Hub</Breadcrumb.Item>
        </Breadcrumb>
      )}

      <div className="giotto-ai-hub__header">
        <div className="giotto-ai-hub__heading">
          <div className="giotto-ai-hub__heading-icon">
            <RobotOutlined />
          </div>
          <div className="giotto-ai-hub__header-copy">
            <Title level={isGlobalMode ? 2 : 3} style={{ margin: 0 }}>
              Giotto AI
            </Title>
            <Paragraph type="secondary" style={{ margin: "4px 0 0" }}>
              {isGlobalMode
                ? "Saldo dei wallet, andamento dei consumi, acquisti add-on e risultati generati in un unico spazio."
                : "Saldo, ordini, utilizzo e risultati AI di questa organizzazione in un unico spazio."}
            </Paragraph>
          </div>
        </div>
        {isOrgMode && <Tag color="blue">Scope organizzazione</Tag>}
      </div>

      <Tabs
        activeKey={activeView}
        className="giotto-ai-hub__tabs"
        items={isGlobalMode ? globalItems : orgItems}
        onChange={handleViewChange}
        tabBarExtraContent={isGlobalMode ? (
          <Text type="secondary">Vista globale</Text>
        ) : null}
      />
    </div>
  );
};

export default GiottoAiHub;
